import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { Lock, Unlock } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { couleurGain, DEFAVORABLE, NEUTRE, NEUTRE_DOUX, signe } from '../../lib/couleurs';
import { annulerImputation, figerImputation } from '../../lib/cri';
import { Carte, btnLight, td, th } from './ui';

const Montant = ({ valeur, colorer = false, unite = 'TND' }) => (
  <span className={`font-data ${colorer ? couleurGain(valeur, NEUTRE) : NEUTRE}`}>
    {colorer && Number(valeur) !== 0 ? `${signe(valeur)} ` : ''}
    {fmt(Math.abs(Number(valeur) || 0), 2)} {unite}
  </span>
);

const Unitaire = ({ valeur, unite }) =>
  valeur === null ? (
    <span className="font-data text-amber-800">Non calculable</span>
  ) : (
    <span className="font-data text-slate-900">
      {fmt(valeur, 2)} <span className="text-[11px] text-slate-500">{unite}</span>
    </span>
  );

const FicheDetail = ({ f, utilisateur, onMaj }) => {
  const figer = async () => {
    const { error } = await figerImputation(f.ofId, utilisateur);
    if (error) return toast.error(error);
    toast.success('Coût standard figé sur l’OF (imputation analytique, aucune écriture comptable).');
    onMaj();
  };
  const annuler = async () => {
    const { error } = await annulerImputation(f.ofId, utilisateur);
    if (error) return toast.error(error);
    toast.success('Imputation annulée.');
    onMaj();
  };

  const prestation = f.type === 'prestation';
  const lignes = prestation
    ? [
        ['A — Coût olives pur (huile du client)', 0, false, 'L’huile appartient au client : ni achat matière, ni produit de vente.'],
        ['B — Produit de prestation (HT, après remises)', f.V, true, 'Montant du service facturé au client.'],
        ['C — Frais industriels standards', -f.C, true, `${fmt(f.Q, 2)} t × ${fmt(f.tauxStandard, 2)} TND/t`],
        ['D — Marge nette industrielle au standard', f.D, true, 'Avant frais commerciaux, financiers et impôt.'],
      ]
    : [
        ['A — Coût olives pur (M)', -f.A, true, 'Montant d’achat convenu, avant retenues et avances.'],
        ['B — Marge brute matière (V − M)', f.B, true, `Valorisation V = ${fmt(f.H, 2)} kg × ${fmt(f.prixReference, 2)} TND/kg (prix de référence du ${f.dateReference}).`],
        ['C — Frais industriels standards (F)', -f.C, true, `${fmt(f.Q, 2)} t × ${fmt(f.tauxStandard, 2)} TND/t`],
        ['D — Marge nette industrielle au standard', f.D, true, 'Avant frais commerciaux, financiers et impôt.'],
      ];

  return (
    <tr data-testid={`cri-fiche-detail-${f.ofId}`} className="bg-slate-50/70">
      <td colSpan={11} className="px-4 py-4">
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2 border border-slate-200 bg-white">
            <p className="border-b border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
              Lecture A → B → C → D — montants totaux en TND
            </p>
            <table className="w-full border-collapse">
              <tbody className="divide-y divide-slate-100">
                {lignes.map(([lib, val, colorer, note]) => (
                  <tr key={lib}>
                    <td className="px-3 py-2">
                      <p className="text-sm font-semibold text-slate-800">{lib}</p>
                      <p className="text-[11px] text-slate-500">{note}</p>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Montant valeur={val} colorer={colorer} />
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="px-3 py-2">
                    <p className="text-sm font-semibold text-slate-800">Régularisation industrielle (ΔF)</p>
                    <p className="text-[11px] text-slate-500">
                      {f.deltaF === 0 ? 'Aucune régularisation comptabilisée à ce jour.' : 'Écart réel / standard réparti au prorata des tonnes traitées.'}
                    </p>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Montant valeur={-f.deltaF} colorer />
                  </td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-3 py-2">
                    <p className="text-sm font-bold text-slate-900">Marge nette industrielle régularisée</p>
                    <p className="text-[11px] text-slate-500">
                      {prestation ? 'Produit de prestation − frais industriels − régularisation' : 'V − M − F − ΔF'} · marge estimée, avant frais commerciaux et financiers
                    </p>
                  </td>
                  <td className="px-3 py-2 text-right text-base font-bold">
                    <Montant valeur={f.margeRegularisee} colorer />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-3">
            <div className="border border-slate-200 bg-white">
              <p className="border-b border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                Indicateurs unitaires ({f.uniteCri})
              </p>
              <div className="divide-y divide-slate-100">
                {[
                  ['Coût matière', f.coutMatiereUnitaire, prestation ? 'Non applicable (huile du client)' : null],
                  ['Coût de transformation', f.coutTransformationUnitaire, null],
                  ['CRI standard', f.criStandardUnitaire, null],
                  ['CRI régularisé', f.criRegulariseUnitaire, null],
                ].map(([k, v, na]) => (
                  <div key={k} className="flex items-center justify-between px-3 py-2">
                    <span className="text-[11px] text-slate-500">{k}</span>
                    {na ? <span className="font-data text-xs text-slate-500">{na}</span> : <Unitaire valeur={v} unite={f.uniteCri} />}
                  </div>
                ))}
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-[11px] text-slate-500">CMP Achat (valeur matière seule)</span>
                  {prestation ? (
                    <span data-testid={`cri-cmp-na-${f.ofId}`} className="font-data text-xs text-slate-500">
                      Non applicable
                    </span>
                  ) : (
                    <span data-testid={`cri-cmp-${f.ofId}`} className="font-data text-sm font-semibold text-slate-900">
                      {f.cmpMatiereKg === null ? 'Non calculable' : `${fmt(f.cmpMatiereKg, 2)} TND/kg`}
                    </span>
                  )}
                </div>
              </div>
              <p className="border-t border-slate-200 px-3 py-2 text-[11px] text-slate-500">
                Le CRI est une information supplémentaire : il ne remplace jamais le CMP Achat des cuves et n’entre pas dans le simulateur d’achat à la bascule.
              </p>
            </div>

            <div className="border border-slate-200 bg-white">
              <p className="border-b border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-[0.1em] text-slate-500">
                Détail du standard appliqué
              </p>
              <div className="divide-y divide-slate-100">
                {(f.detailTaux ?? []).map((c) => (
                  <div key={c.categorie} className="flex items-center justify-between px-3 py-1.5">
                    <span className="text-[11px] text-slate-600">{c.categorie}</span>
                    <span className="font-data text-xs text-slate-900">
                      {fmt(c.tauxTndParTonne, 2)} TND/t → {fmt(c.montant ?? f.Q * c.tauxTndParTonne, 2)} TND
                    </span>
                  </div>
                ))}
                {!(f.detailTaux ?? []).length && <p className="px-3 py-2 text-xs text-amber-800">Aucun standard industriel applicable à la date de cet OF.</p>}
              </div>
              <div className="border-t border-slate-200 px-3 py-2">
                {f.fige ? (
                  <button data-testid={`cri-annuler-${f.ofId}`} type="button" onClick={annuler} className={btnLight}>
                    <Unlock size={13} /> Annuler l’imputation
                  </button>
                ) : (
                  <button data-testid={`cri-figer-${f.ofId}`} type="button" onClick={figer} className={btnLight}>
                    <Lock size={13} /> Figer le coût standard
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

export const FichesCri = ({ fiches, utilisateur, onMaj }) => {
  const [ouvert, setOuvert] = useState(null);

  return (
    <Carte
      testid="cri-fiches"
      titre="Fiches de coût de revient par OF"
      sous="Poids, rendements, huile obtenue, bonus, version du standard et statut du coût — puis la lecture A → B → C → D. Cliquez une ligne pour ouvrir la fiche."
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1160px] border-collapse">
          <thead className="bg-slate-50">
            <tr>
              <th className={th}>OF</th>
              <th className={th}>Date</th>
              <th className={th}>Type</th>
              <th className={th}>Olives (t)</th>
              <th className={th}>Huile (kg)</th>
              <th className={th}>Rdt réel</th>
              <th className={th}>Bonus (kg)</th>
              <th className={th}>Std</th>
              <th className={th}>Coût matière</th>
              <th className={th}>CRI régularisé</th>
              <th className={th}>Statut coût</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {fiches.length === 0 && (
              <tr>
                <td className={`${td} text-slate-500`} colSpan={11}>
                  Aucun ordre de fabrication sur cette campagne.
                </td>
              </tr>
            )}
            {fiches.map((f) => (
              <Fragment key={f.ofId}>
                <tr
                  data-testid={`cri-fiche-${f.ofId}`}
                  onClick={() => setOuvert((p) => (p === f.ofId ? null : f.ofId))}
                  className="cursor-pointer transition-colors duration-150 hover:bg-slate-50"
                >
                  <td className={`${td} font-semibold`}>{f.numero}</td>
                  <td className={td}>{f.termineLe || f.date}</td>
                  <td className={td}>
                    <span className={`border px-1.5 py-0.5 font-data text-[11px] uppercase ${f.type === 'prestation' ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-slate-300 bg-slate-100 text-slate-700'}`}>
                      {f.type === 'prestation' ? 'Prestation' : 'Achat'}
                    </span>
                  </td>
                  <td className={td}>{fmt(f.Q, 2)}</td>
                  <td className={td}>{f.H > 0 ? fmt(f.H, 2) : <span className="text-amber-800">—</span>}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{f.rendement ? `${fmt(f.rendement, 2)} %` : '—'}</td>
                  <td className={td}>{f.bonusKg ? fmt(f.bonusKg, 2) : '—'}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{f.standardVersion ? `v${f.standardVersion}` : '—'}</td>
                  <td className={td}>
                    {f.type === 'prestation' ? (
                      <span className="text-xs text-slate-500">Non applicable</span>
                    ) : (
                      <span className={DEFAVORABLE}>{fmt(f.A, 2)}</span>
                    )}
                  </td>
                  <td className={td}>
                    <Unitaire valeur={f.criRegulariseUnitaire} unite={f.uniteCri} />
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`border px-1.5 py-0.5 font-data text-[11px] uppercase ${
                        f.statutCout === 'Régularisé'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : f.statutCout === 'Standard figé'
                            ? 'border-slate-300 bg-slate-100 text-slate-700'
                            : 'border-amber-300 bg-amber-50 text-amber-800'
                      }`}
                    >
                      {f.statutCout}
                    </span>
                    {f.prixMatiereProvisoire && <span className="ml-1 border border-amber-300 bg-amber-50 px-1 font-data text-[10px] uppercase text-amber-800">Prix provisoire</span>}
                  </td>
                </tr>
                {ouvert === f.ofId && <FicheDetail f={f} utilisateur={utilisateur} onMaj={onMaj} />}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Carte>
  );
};
